import { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';

import { AsyncContent } from '~/components/AsyncContent';
import { useSelectedEmployeeContext } from '~/components/SelectedEmployee';
import { useAppearance } from '~/data/appearance';
import { useAuthentication } from '~/data/user/useAuthentication';
import { LeftSideMenu } from '~/features/Sidebars/LeftSideMenu';
import {
  DispatchReduxFetch,
  useInitializeCommonReduxData,
} from '~/hooks/initializeData';
import { useInterval } from '~/hooks/useInterval';
import { LibraryComponentProviders } from '~/library/components/LibraryComponentProviders';
import { Providers } from '~/Providers';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

export type LeftSideMenuProps = {
  employeeId?: number;
};

export const WrappedLeftSideMenu = (props: LeftSideMenuProps) => {
  const { isAuthenticated, failToLoad } = useAuthentication();

  const {
    requiredStatus: { isInitializing, percentage },
  } = useInitializeCommonReduxData({
    required: [
      DispatchReduxFetch.CustomAppearance,
      DispatchReduxFetch.AllOrgUnits,
      DispatchReduxFetch.CompanyData,
      DispatchReduxFetch.ApplicationConfig,
    ],
  });

  if (isInitializing || !isAuthenticated) {
    return <LoadingView>{percentage}</LoadingView>;
  }

  return (
    <LibraryComponentProviders>
      <AsyncContent
        isLoading={!isAuthenticated}
        failedToLoad={failToLoad}
        fullHeight={true}
      >
        <PortalLeftSideMenu {...props} />
      </AsyncContent>
    </LibraryComponentProviders>
  );
};

export const PortalLeftSideMenu = (props: LeftSideMenuProps) => {
  const { employeeId } = props;
  const { selectedEmployeeId, refreshSelectedEmployeeId } =
    useSelectedEmployeeContext();

  useInterval(
    !!employeeId && employeeId !== selectedEmployeeId,
    () => {
      refreshSelectedEmployeeId();
    },
    1000,
  );

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const onOpen = useCallback(() => {
    setSidebarOpen(true);
  }, [setSidebarOpen]);
  const onClose = useCallback(() => {
    setSidebarOpen(false);
  }, [setSidebarOpen]);

  const appearance = useAppearance();

  if (appearance.isLoading && appearance.isEnabled) {
    return <LoadingView />;
  }

  return (
    <LeftSideMenu
      open={sidebarOpen}
      handleDrawerOpen={onOpen}
      handleDrawerClose={onClose}
    />
  );
};

export const renderLeftSideMenu = (
  containerId: string,
  props: LeftSideMenuProps,
) => {
  ReactDOM.render(
    <Providers>
      <WrappedLeftSideMenu {...props} />
    </Providers>,
    document.getElementById(containerId),
  );
};
